import { Server, Cpu, DollarSign, TrendingUp, Info, Lightbulb, Target, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  EstimateQualityIndicator, 
  type EstimateQuality,
  type RefinementStep,
  calculateRefinementSteps 
} from './ConfidenceIndicator';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useState } from 'react';

import { TeamType, ResponseSpeed } from './QuickStartCard';
export type { TeamType, ResponseSpeed };

interface InstantResultsProps {
  serverCount: number;
  gpuCount: number;
  estimatedCloudCost?: number;
  breakEvenMonths?: number | null; // Calculated break-even in months
  confidenceLevel: EstimateQuality;
  confidencePercentage: number; // Still accepted for backwards compat, but not displayed
  confidenceNextSteps: string[];
  isLoading?: boolean;
  className?: string;
  // Completion flags for detailed refinement steps
  hasModelSelected?: boolean;
  hasCustomPersonas?: boolean;
  hasPrecisionTuned?: boolean;
  // Context for explanation generation
  teamType?: TeamType;
  responseSpeed?: ResponseSpeed;
  concurrentUsers?: number;
  chatMemoryTurns?: number;
  modelParamsB?: number;
}

// Generate explanation bullets based on current state
function generateExplanationBullets(props: {
  teamType?: TeamType;
  responseSpeed?: ResponseSpeed;
  concurrentUsers?: number;
  chatMemoryTurns?: number;
  modelParamsB?: number;
}): string[] {
  const bullets: string[] = [];
  const { teamType, responseSpeed, concurrentUsers, chatMemoryTurns, modelParamsB } = props;

  // Team type explanations
  if (teamType === 'research-academic') {
    bullets.push('Research teams have higher context length needs, increasing memory pressure.');
  } else if (teamType === 'software-dev') {
    bullets.push('Development teams generate longer outputs, increasing throughput demand.');
  } else if (teamType === 'customer-support') {
    bullets.push('Support teams prioritize quick responses with shorter conversations.');
  } else if (teamType === 'general-knowledge') {
    bullets.push('Mixed teams have varied usage patterns, averaging out resource demands.');
  }

  // Concurrent users explanations
  if (concurrentUsers && concurrentUsers >= 5000) {
    bullets.push(`High concurrency (${concurrentUsers.toLocaleString()} users) is the primary sizing driver.`);
  } else if (concurrentUsers && concurrentUsers >= 1000) {
    bullets.push(`${concurrentUsers.toLocaleString()} concurrent users drives peak demand and queueing risk.`);
  } else if (concurrentUsers && concurrentUsers >= 100) {
    bullets.push(`${concurrentUsers.toLocaleString()} concurrent users allows efficient GPU batching.`);
  }

  // Response speed explanations
  if (responseSpeed === 'fast') {
    bullets.push('Fast response speed requires more GPUs to maintain low latency.');
  } else if (responseSpeed === 'balanced') {
    bullets.push('Balanced response speed targets good UX without max-cost hardware.');
  } else if (responseSpeed === 'relaxed') {
    bullets.push('Relaxed latency tolerance enables efficient batching, reducing hardware needs.');
  }

  // Chat memory / turns
  if (chatMemoryTurns && chatMemoryTurns >= 10) {
    bullets.push(`Extended chat memory (${chatMemoryTurns} turns) significantly increases KV cache requirements.`);
  }

  // Model size
  if (modelParamsB && modelParamsB >= 70) {
    bullets.push(`Large model (${modelParamsB}B parameters) requires substantial memory per GPU.`);
  }

  // Return top 3
  return bullets.slice(0, 3);
}

// Rank input levers by impact
function rankInputLevers(props: {
  concurrentUsers?: number;
  responseSpeed?: ResponseSpeed;
  chatMemoryTurns?: number;
  modelParamsB?: number;
}): { label: string; impact: 'high' | 'medium' | 'low' }[] {
  const { concurrentUsers, responseSpeed, chatMemoryTurns, modelParamsB } = props;

  const levers: { label: string; impact: 'high' | 'medium' | 'low'; score: number }[] = [
    {
      label: 'Concurrent users',
      impact: (concurrentUsers ?? 0) >= 1000 ? 'high' : (concurrentUsers ?? 0) >= 200 ? 'medium' : 'low',
      score: (concurrentUsers ?? 0) >= 1000 ? 3 : (concurrentUsers ?? 0) >= 200 ? 2 : 1,
    },
    {
      label: 'Response speed profile',
      impact: responseSpeed === 'fast' ? 'high' : responseSpeed === 'relaxed' ? 'low' : 'medium',
      score: responseSpeed === 'fast' ? 3 : responseSpeed === 'relaxed' ? 1 : 2,
    },
    {
      label: 'Chat memory (context length)',
      impact: (chatMemoryTurns ?? 0) >= 10 ? 'high' : (chatMemoryTurns ?? 0) >= 5 ? 'medium' : 'low',
      score: (chatMemoryTurns ?? 0) >= 10 ? 3 : (chatMemoryTurns ?? 0) >= 5 ? 2 : 1,
    },
    {
      label: 'Model size',
      impact: (modelParamsB ?? 0) >= 70 ? 'high' : (modelParamsB ?? 0) >= 30 ? 'medium' : 'low',
      score: (modelParamsB ?? 0) >= 70 ? 3 : (modelParamsB ?? 0) >= 30 ? 2 : 1,
    },
  ];

  // Sort by score descending, return top 3
  return levers
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(({ label, impact }) => ({ label, impact }));
}

export function InstantResults({
  serverCount,
  gpuCount,
  estimatedCloudCost,
  breakEvenMonths,
  confidenceLevel,
  confidencePercentage: _confidencePercentage, // Ignored
  confidenceNextSteps,
  isLoading,
  className,
  hasModelSelected = false,
  hasCustomPersonas = false,
  hasPrecisionTuned = false,
  teamType,
  responseSpeed,
  concurrentUsers,
  chatMemoryTurns,
  modelParamsB,
}: InstantResultsProps) {
  // Generate detailed refinement steps
  const refinementSteps = calculateRefinementSteps({
    hasBasicInputs: true, // Always true if we're showing results
    hasCustomPersonas,
    hasModelSelected,
    hasPrecisionTuned,
  });

  // Filter to only show incomplete steps
  const incompleteSteps = refinementSteps.filter(s => !s.completed);

  // Generate explanation bullets
  const explanationBullets = generateExplanationBullets({
    teamType,
    responseSpeed,
    concurrentUsers,
    chatMemoryTurns,
    modelParamsB,
  });

  // Rank input levers
  const topLevers = rankInputLevers({
    concurrentUsers,
    responseSpeed,
    chatMemoryTurns,
    modelParamsB,
  });

  return (
    <Card className={cn('bg-card border-border shadow-sm', className)}>
      <CardContent className="p-6 space-y-4">
        {/* Header - honest framing */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-foreground">
              Your Starting Estimate
            </h3>
            <p className="text-sm text-muted-foreground">
              Based on {serverCount === 1 ? 'your inputs' : 'typical usage patterns'} — refine below for accuracy
            </p>
          </div>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Servers */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <Server className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">
                Servers
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-3.5 w-3.5 text-muted-foreground/60" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p>Dell XE7740 with 8× Intel Gaudi 3 accelerators each. This is a starting estimate — actual needs depend on your specific workload.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className={cn(
              'text-3xl font-bold text-foreground tabular-nums transition-opacity',
              isLoading && 'opacity-50'
            )}>
              {serverCount}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Dell XE7740
            </div>
          </div>

          {/* GPUs */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-secondary/10 to-secondary/5 border border-secondary/20">
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="h-5 w-5 text-secondary-foreground" />
              <span className="text-sm font-medium text-muted-foreground">
                Accelerators
              </span>
            </div>
            <div className={cn(
              'text-3xl font-bold text-foreground tabular-nums transition-opacity',
              isLoading && 'opacity-50'
            )}>
              {gpuCount}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Intel Gaudi 3
            </div>
          </div>

          {/* Cloud Equiv (if provided) */}
          {estimatedCloudCost !== undefined && (
            <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-muted-foreground">
                  Cloud Equiv
                </span>
              </div>
              <div className={cn(
                'text-3xl font-bold text-foreground tabular-nums transition-opacity',
                isLoading && 'opacity-50'
              )}>
                ${Math.round(estimatedCloudCost / 1000)}k
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                per month
              </div>
            </div>
          )}

          {/* Indicative Timeline */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-muted-foreground">
                Break-even
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-3.5 w-3.5 text-muted-foreground/60" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p>Approximate time to recover on-prem investment vs. cloud. Varies significantly based on utilization, cloud provider, and pricing.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className={cn(
              'text-3xl font-bold text-foreground tabular-nums transition-opacity',
              isLoading && 'opacity-50'
            )}>
              {breakEvenMonths !== null && breakEvenMonths !== undefined
                ? `~${breakEvenMonths}mo`
                : '—'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {breakEvenMonths !== null && breakEvenMonths !== undefined
                ? 'vs cloud'
                : 'Cloud may be cheaper'}
            </div>
          </div>
        </div>

        {/* Understand This Estimate - Collapsible Section */}
        {(explanationBullets.length > 0 || topLevers.length > 0 || incompleteSteps.length > 0) && (
          <Collapsible className="pt-3 border-t border-border">
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-foreground hover:text-primary transition-colors group">
              <span>Understand this estimate</span>
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 pt-2">
              {/* Why this recommendation */}
              {explanationBullets.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-foreground">Why this recommendation</span>
                  </div>
                  <ul className="space-y-1.5 ml-6">
                    {explanationBullets.map((bullet, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-muted-foreground/60 mt-1">•</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What changes the answer fastest */}
              {topLevers.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium text-foreground">What changes the answer fastest</span>
                  </div>
                  <ol className="space-y-1 ml-6">
                    {topLevers.map((lever, idx) => (
                      <li key={lever.label} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="text-muted-foreground/60 w-4">{idx + 1}.</span>
                        <span>{lever.label}</span>
                        <span className={cn(
                          'text-xs px-1.5 py-0.5 rounded',
                          lever.impact === 'high' && 'bg-red-500/10 text-red-600 dark:text-red-400',
                          lever.impact === 'medium' && 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
                          lever.impact === 'low' && 'bg-green-500/10 text-green-600 dark:text-green-400',
                        )}>
                          {lever.impact}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Refinement Guidance */}
              {incompleteSteps.length > 0 && (
                <EstimateQualityIndicator
                  quality={confidenceLevel}
                  refinements={refinementSteps}
                />
              )}

              {/* Completed state */}
              {incompleteSteps.length === 0 && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <span className="font-medium">Fully customized</span>
                  <span className="text-muted-foreground">— ready for detailed review below</span>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
